import type { WorkflowView } from '@/lib/api/generated/model';

import { countDeclaredSteps } from './hooks/useWorkflows';
import { tasksOfWorkflow } from './workflowTasks';
import type { WorkflowScopedTask } from './workflowTasks';

/** What deleting one workflow would take with it. */
export type DeletionSummary = {
  phases: number;
  steps: number;
  /** In the order the workspace returned them. */
  taskTitles: string[];
};

/**
 * The minimum a task must provide to be counted against a workflow.
 *
 * Structural rather than `TaskView`, following `SelectionScope` in
 * `builder/selection.ts`: this module needs two fields, and taking the whole
 * view would make every test here construct a task no assertion looks at.
 */
export type DeletableTask = WorkflowScopedTask;

/**
 * What a delete-workflow confirmation has to say, worked out from data the page
 * has already loaded.
 *
 * Deleting a workflow is the one irreversible action in this product — the
 * service removes it "and everything positioned on it" — and the dialog that
 * guards it said only that tasks would go, never how many or which. Every number
 * needed to fix that is in the workspace aggregate the list is already rendering
 * from, so this is arithmetic rather than a request.
 *
 * Tasks are filtered by `workflow_id` rather than assumed to belong: the
 * aggregate carries every task in the workspace, and counting another workflow's
 * work into this warning would be worse than counting none.
 *
 * Pure, so the counting is tested without rendering a dialog — and so the
 * dialog itself has nothing to test but its prose.
 */
export function deletionSummary(
  workflow: WorkflowView,
  tasks: ReadonlyArray<DeletableTask>,
): DeletionSummary {
  return {
    phases: workflow.phases.length,
    // The same counter the list row uses, rather than a second way of counting
    // the same thing: `start_step` and `done_step` are synthesized and live
    // outside the phases, so a naive sum would report steps the author never
    // wrote.
    steps: countDeclaredSteps(workflow.phases),
    taskTitles: tasksOfWorkflow(tasks, workflow.id).map((task) => task.title),
  };
}
